import React, { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Button,
  Typography,
  Textarea,
} from "@material-tailwind/react";
import {
  XMarkIcon,
  CloudArrowUpIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import {
  reUploadDeliveryProof,
  getRejectedDeliveryAssignments,
} from "@/store/slices/newDeliverySlice";
import { toast } from "react-toastify";
import ShowLoader from "@/components/Loader";
import { useTranslation } from "react-i18next";

const MAX_IMAGES = 10;

export default function RejectedProofModal({
  open,
  onClose,
  items = [],
  loading = false,
}) {
  const dispatch = useDispatch();
  const { reupload_loading } = useSelector((state) => state.delivery);

  // Use delivery.json
  const { t } = useTranslation("delivery");

  const [selectedItem, setSelectedItem] = useState(null);

  const [images, setImages] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [remark, setRemark] = useState("");

  const resetForm = () => {
    setSelectedItem(null);
    setImages([]);
    setReceipts([]);
    setRemark("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    if (images.length + files.length > MAX_IMAGES) {
      toast.warn(
        t("maximum_images_allowed", {
          count: MAX_IMAGES,
        })
      );
      return;
    }

    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);

    const mapped = files.map((file) => ({
      file,
      name: file.name,
    }));

    setReceipts((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeReceipt = (index) => {
    setReceipts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const r = remark.trim();

    if (!selectedItem) return;

    if (images.length === 0 && receipts.length === 0) {
      toast.error(t("please_upload_at_least_one_image_or_receipt"));
      return;
    }

    if (r.length < 5) {
      toast.error(t("remark_must_be_at_least_5_characters"));
      return;
    }

    try {
      const res = await dispatch(
        reUploadDeliveryProof({
          delivery_assigned_orders_id:
            selectedItem.delivery_assigned_orders_id,
          remark: r,
          images,
          receipts,
        })
      ).unwrap();

      if (res?.status) {
        toast.success(
          res.message || t("proof_reuploaded_successfully")
        );

        await dispatch(getRejectedDeliveryAssignments());

        resetForm();
      } else {
        toast.error(res?.message || t("failed_to_upload_proof"));
      }
    } catch (err) {
      toast.error(err?.message || t("failed_to_upload_proof"));
    }
  };

  return (
    <Dialog
      open={open}
      handler={handleClose}
      size="lg"
      className="rounded-2xl"
    >
      {reupload_loading && <ShowLoader />}

      <DialogHeader className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {selectedItem && (
            <button
              type="button"
              onClick={resetForm}
              className="p-1 rounded hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
            </button>
          )}

          <Typography variant="h6" className="font-bold">
            {selectedItem
              ? t("reupload_delivery_proof")
              : t("rejected_delivery_proofs")}
          </Typography>
        </div>

        <Button variant="text" className="p-2" onClick={handleClose}>
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </DialogHeader>

      <DialogBody className="max-h-[75vh] overflow-y-auto">
        {!selectedItem ? (
          loading ? (
            <div className="text-center py-8 text-gray-500">
              {t("loading")}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              {t("no_rejected_proofs_found")}
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.delivery_assigned_orders_id}
                  className="border border-red-100 rounded-xl p-4 bg-red-50/40"
                >
                  <div className="flex gap-4">
                    <img
                      src={
                        item.product_image ||
                        "/img/placeholder.png"
                      }
                      alt={item.product_name}
                      className="h-20 w-20 rounded-lg object-cover border border-gray-200 bg-white"
                    />

                    <div className="flex-1 min-w-0">
                      <Typography className="font-semibold text-gray-900">
                        {item.product_name || t("product")}
                      </Typography>

                      <Typography className="text-sm text-gray-600">
                        {t("po_id")}: {item.order_id}
                      </Typography>

                      <Typography className="text-sm text-gray-600">
                        {t("assigned_qty")}: {item.assigned_quantity}
                      </Typography>

                      <div className="mt-2 inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 capitalize">
                        {item.admin_status}
                      </div>

                      {item.rejected_reason && (
                        <div className="mt-3 text-sm text-red-700 break-all">
                          <span className="font-semibold">
                            {t("reason")}:
                          </span>{" "}
                          {item.rejected_reason}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button
                      size="sm"
                      className="bg-[#F4B11F] text-white hover:bg-[#E0A216]"
                      onClick={() => setSelectedItem(item)}
                    >
                      {t("reupload_proof")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4 rounded-xl border p-4 bg-gray-50">
              <img
                src={
                  selectedItem.product_image ||
                  "/img/placeholder.png"
                }
                alt={selectedItem.product_name}
                className="h-16 w-16 rounded-lg object-cover border"
              />

              <div>
                <Typography className="font-semibold text-gray-900">
                  {selectedItem.product_name}
                </Typography>

                <Typography className="text-sm text-gray-600">
                  {t("order")}: {selectedItem.order_id}
                </Typography>

                <Typography className="text-sm text-gray-600">
                  {t("assigned_qty")}:{" "}
                  {selectedItem.assigned_quantity}
                </Typography>
              </div>
            </div>

            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <Typography className="text-sm font-semibold text-red-700">
                {t("rejection_reason_by_admin")}
              </Typography>

              <Typography className="text-sm text-red-600 mt-1">
                {selectedItem.rejected_reason ||
                  t("no_reason_provided")}
              </Typography>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <Typography className="font-medium">
                  {t("delivery_proof_images")}
                </Typography>

                <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                  {images.length}/{MAX_IMAGES}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className="relative rounded-xl overflow-hidden border bg-white"
                  >
                    <img
                      src={img.preview}
                      alt={t("preview")}
                      className="h-32 w-full object-cover"
                    />

                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow"
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}

                {images.length < MAX_IMAGES && (
                  <label className="h-32 border-2 border-dashed border-[#F4B11F] rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-[#F4B11F]/5">
                    <CloudArrowUpIcon className="h-8 w-8 text-[#F4B11F]" />

                    <span className="text-xs mt-1 text-[#F4B11F]">
                      {t("add_image")}
                    </span>

                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      multiple
                      hidden
                      onChange={handleImageUpload}
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Typography className="font-medium mb-3">
                {t("delivery_receipts")}
              </Typography>

              <div className="space-y-3">
                {receipts.map((receipt, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-[#FFDEDE] bg-[#FFF1F1] p-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentTextIcon className="h-5 w-5 text-gray-600 shrink-0" />

                      <span className="text-sm truncate">
                        {receipt.name}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeReceipt(index)}
                      className="p-1 hover:bg-white rounded-full"
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>

              <label className="inline-flex items-center gap-2 mt-4 px-4 py-2 border-2 border-[#F4B11F] rounded-lg text-[#F4B11F] cursor-pointer hover:bg-[#F4B11F] hover:text-white transition">
                <ArrowUpTrayIcon className="h-5 w-5" />

                {t("add_receipt")}

                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  multiple
                  hidden
                  onChange={handleReceiptUpload}
                />
              </label>
            </div>

            <div>
              <Typography className="font-medium mb-2">
                {t("remark")}
              </Typography>

              <Textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={4}
                maxLength={50}
                placeholder={t("enter_remark")}
                className="focus:border-[#F4B11F] focus:ring-[#F4B11F]"
              />

              <div className="text-right text-xs text-gray-400 mt-1">
                {remark.trim().length}/50
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="text"
                onClick={resetForm}
                className="text-gray-600"
              >
                {t("back")}
              </Button>

              <Button
                onClick={handleSubmit}
                className="bg-[#F4B11F] text-white hover:bg-[#E0A216]"
                disabled={reupload_loading}
              >
                {reupload_loading
                  ? t("uploading")
                  : t("submit_reupload")}
              </Button>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}